/**
 * SharedReport — Public viewer for report snapshots
 * Renders the same interactive table as the Final Report (search, sort, expand drilldowns)
 * No authentication required. Password gate if the snapshot is protected.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  FileText, Loader2, Lock, ChevronDown, ChevronRight,
  ArrowDown, ArrowUp, AlertTriangle, Shield, Sun, Moon,
} from 'lucide-react';
import SharedClaimV3 from '../components/raketech/SharedClaimV3';
import { isSharedV3 } from '../lib/raketechShareV3';

// ---------------------------------------------------------------------------
// Types (mirrored from RaketechFinalReport)
// ---------------------------------------------------------------------------
interface InvoiceMonth { month: string; ftds: number; revenue: number; }

interface ComparisonIssue {
  type: string; severity: string; accountId: string; username: string;
  brand: string; partner: string; offer: string; geo: string; currency: string; details: string;
  invoiceFTDs?: number; invoiceRevenue?: number; postReconInvoiceRevenue?: number;
  reconciliationEarnings?: number; reconciliationEarningsOriginal?: number; reconciliationRows?: number;
  adwstatsCommission?: number; adwstatsCommissionOriginal?: number; adwstatsCurrency?: string;
  adwstatsAccountId?: string; adwstatsAccountName?: string; adwstatsMatchMethod?: string; adwstatsDataSynced?: boolean;
  partnerCommission?: number; partnerCommissionOriginal?: number; partnerCommissionOurShare?: number;
  manualCheckAmount?: number; manualCheckComment?: string; manualCheckSource?: string;
  revenueDifference?: number; percentageDifference?: number; exchangeRate?: number;
  missingInInvoice?: boolean; missingInReconciliation?: boolean;
  invoiceMonthly?: InvoiceMonth[];
  invoiceEntries?: { label: string; ftds: number; revenue: number; monthly: InvoiceMonth[] }[];
  reconMonthly?: { month: string; earnings: number; ftds: number }[];
  syncedMonthly?: { month: string; commission: number; ftds: number }[];
}

interface ReportRow {
  issue: ComparisonIssue;
  reconInvoiced: number; postReconInvoiced: number; totalInvoiced: number;
  incomeAmount: number; incomeSource: string; incomeOriginal: number;
  incomeOriginalCurrency?: string; incomeOriginalAmount?: number;
  acPostRecon: number; totalEarned: number; underpaid: number; underpaidPct: number;
}

interface SnapshotData {
  rows: ReportRow[];
  totals: { reconInvoiced: number; postReconInvoiced: number; totalInvoiced: number; income: number; acPost: number; totalEarned: number; underpaid: number; count: number };
  hasAcData: boolean;
  period: { recon: string; postRecon: string | null };
  acMonthlyByAccountId?: Record<string, { month: string; commission: number }[]>;
}

interface SnapshotMeta {
  title: string; description: string | null; lastMonth: string;
  createdAt: string; viewCount: number; hasPassword: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const fmtCurrency = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v);

const fmtOriginal = (v: number, currency: string) => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${currency} ${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
};

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const fmtMonth = (m: string) => {
  if (!m || m === 'unknown') return m;
  if (/^\d{4}-\d{2}$/.test(m)) {
    const [y, mo] = m.split('-');
    return `${MONTH_SHORT[parseInt(mo, 10) - 1] || mo} ${y}`;
  }
  return m;
};

const monthToSortKey = (m: string): string => {
  if (/^\d{4}-\d{2}$/.test(m)) return m;
  const slashMatch = m.match(/^\d{2}\/(\d{2})\/(\d{4})$/);
  if (slashMatch) return `${slashMatch[2]}-${slashMatch[1]}`;
  const parts = m.trim().split(/\s+/);
  if (parts.length === 2) {
    const idx = MONTH_NAMES.indexOf(parts[0]);
    if (idx >= 0) return `${parts[1]}-${String(idx + 1).padStart(2, '0')}`;
  }
  return m;
};

const sortMonthly = (months: InvoiceMonth[]): InvoiceMonth[] =>
  [...months].sort((a, b) => monthToSortKey(a.month).localeCompare(monthToSortKey(b.month)));

type SortField = 'partner' | 'accountId' | 'reconInvoiced' | 'postReconInvoiced' | 'totalInvoiced' | 'income' | 'acPostRecon' | 'totalEarned' | 'underpaid' | 'pct';
type SortDir = 'asc' | 'desc';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const SharedReport: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [meta, setMeta] = useState<SnapshotMeta | null>(null);
  const [data, setData] = useState<SnapshotData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('underpaid');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [darkMode, setDarkMode] = useState(true);

  const fetchReport = useCallback(async (pw?: string) => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    try {
      let resp: Response;
      if (pw) {
        resp = await fetch(`/api/public/report/${slug}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: pw }),
        });
      } else {
        resp = await fetch(`/api/public/report/${slug}`);
      }
      const json = await resp.json() as any;
      if (!json.success) {
        if (resp.status === 403) { setPasswordError(true); setLoading(false); return; }
        if (resp.status === 404) { setError('Report not found'); setLoading(false); return; }
        throw new Error(json.error || 'Failed to load');
      }
      if (json.meta) setMeta(json.meta);
      if (json.data) {
        setData(json.data);
        setNeedsPassword(false);
      } else if (json.meta?.hasPassword) {
        setNeedsPassword(true);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleUnlock = () => {
    setPasswordError(false);
    fetchReport(password);
  };

  const toggleRow = (key: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const rows = data?.rows || [];
  const totals = data?.totals || { reconInvoiced: 0, postReconInvoiced: 0, totalInvoiced: 0, income: 0, acPost: 0, totalEarned: 0, underpaid: 0, count: 0 };
  const hasAcData = data?.hasAcData || false;
  const acMonthlyMap = useMemo(() => {
    if (!data?.acMonthlyByAccountId) return new Map<string, { month: string; commission: number }[]>();
    return new Map(Object.entries(data.acMonthlyByAccountId));
  }, [data]);

  const filteredRows = useMemo(() => {
    let items = rows;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      items = items.filter(r =>
        r.issue.partner?.toLowerCase().includes(q) ||
        r.issue.brand?.toLowerCase().includes(q) ||
        r.issue.accountId?.toLowerCase().includes(q) ||
        r.issue.username?.toLowerCase().includes(q) ||
        r.issue.geo?.toLowerCase().includes(q) ||
        r.issue.adwstatsAccountName?.toLowerCase().includes(q)
      );
    }
    const getVal = (r: ReportRow): number | string => {
      switch (sortField) {
        case 'partner': return (r.issue.partner || '').toLowerCase();
        case 'accountId': return r.issue.accountId || '';
        case 'reconInvoiced': return r.reconInvoiced;
        case 'postReconInvoiced': return r.postReconInvoiced;
        case 'totalInvoiced': return r.totalInvoiced;
        case 'income': return r.incomeAmount;
        case 'acPostRecon': return r.acPostRecon;
        case 'totalEarned': return r.totalEarned;
        case 'underpaid': return r.underpaid;
        case 'pct': return r.underpaidPct;
        default: return 0;
      }
    };
    return [...items].sort((a, b) => {
      const va = getVal(a); const vb = getVal(b);
      const cmp = typeof va === 'string' ? va.localeCompare(vb as string) : (va as number) - (vb as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, searchTerm, sortField, sortDir]);

  const SortHeader: React.FC<{ field: SortField; label: string; align?: string; className?: string }> = ({ field, label, align, className }) => (
    <th
      className={`px-3 py-2.5 font-medium cursor-pointer select-none whitespace-nowrap ${align === 'right' ? 'text-right' : 'text-left'} ${className || ''} ${darkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-gray-500 hover:text-gray-700'}`}
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortField === field && (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </span>
    </th>
  );

  const sourceColor = (src: string) => {
    if (darkMode) {
      if (src.includes('Synced')) return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
      if (src.includes('Manual')) return 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20';
      return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
    }
    if (src.includes('Synced')) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
    if (src.includes('Manual')) return 'text-cyan-700 bg-cyan-50 border-cyan-200';
    return 'text-amber-700 bg-amber-50 border-amber-200';
  };

  const sourceShort = (src: string) => {
    if (src.includes('Synced')) return 'Synced -5%';
    if (src.includes('Manual')) return 'Manual -5%';
    return 'Recon';
  };

  const bg = darkMode ? 'bg-[hsl(225,15%,6%)]' : 'bg-gray-50';
  const cardBg = darkMode ? 'bg-[hsl(225,15%,10%)]' : 'bg-white';
  const cardBorder = darkMode ? 'border-[hsl(225,15%,18%)]' : 'border-gray-200';
  const headBg = darkMode ? 'bg-[hsl(225,15%,8%)]' : 'bg-gray-100';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-zinc-400' : 'text-gray-500';
  const textMuted = darkMode ? 'text-zinc-500' : 'text-gray-400';
  const textValue = darkMode ? 'text-zinc-300' : 'text-gray-700';
  const rowHover = darkMode ? 'hover:bg-[hsl(225,15%,12%)]' : 'hover:bg-gray-50';
  const divider = darkMode ? 'divide-[hsl(225,15%,13%)]' : 'divide-gray-100';
  const inputBg = darkMode ? 'bg-[hsl(225,15%,12%)] border-[hsl(225,15%,20%)] text-white placeholder-zinc-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400';
  const drillBg = darkMode ? 'bg-[hsl(225,15%,8%)]' : 'bg-gray-50';
  const drillBorder = darkMode ? 'border-[hsl(225,15%,15%)]' : 'border-gray-200';

  // Loading / error / password states
  if (loading) {
    return (
      <div className={`min-h-screen ${bg} flex items-center justify-center`}>
        <Loader2 className="h-6 w-6 animate-spin text-indigo-400 mr-3" />
        <span className={textSecondary}>Loading report...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen ${bg} flex items-center justify-center`}>
        <div className={`${cardBg} border ${cardBorder} rounded-2xl p-8 text-center max-w-sm`}>
          <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-4" />
          <h2 className={`text-lg font-semibold ${textPrimary} mb-2`}>Report Not Available</h2>
          <p className={`text-sm ${textSecondary}`}>{error}</p>
        </div>
      </div>
    );
  }

  if (needsPassword && !data) {
    return (
      <div className={`min-h-screen ${bg} flex items-center justify-center`}>
        <div className={`${cardBg} border ${cardBorder} rounded-2xl p-8 max-w-sm w-full mx-4`}>
          <div className="text-center mb-6">
            <Lock className="h-10 w-10 text-indigo-400 mx-auto mb-3" />
            <h2 className={`text-lg font-semibold ${textPrimary}`}>{meta?.title || 'Protected Report'}</h2>
            {meta?.description && <p className={`text-sm ${textSecondary} mt-1`}>{meta.description}</p>}
            <p className={`text-xs ${textMuted} mt-2`}>This report requires a password to view.</p>
          </div>
          <div className="space-y-3">
            <input
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); setPasswordError(false); }}
              onKeyDown={e => e.key === 'Enter' && handleUnlock()}
              placeholder="Enter password"
              className={`w-full px-3 py-2.5 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-indigo-500/50 ${inputBg} ${passwordError ? 'border-red-500' : ''}`}
            />
            {passwordError && <p className="text-xs text-red-400">Invalid password. Please try again.</p>}
            <button
              onClick={handleUnlock}
              disabled={!password}
              className="w-full px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Unlock Report
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // v3 snapshots carry their own shape and their own viewer. Everything above —
  // the fetch, the password gate and the theme — is common to both.
  if (isSharedV3(data)) {
    return (
      <div className={`min-h-screen ${bg} transition-colors`}>
        <div className={`${cardBg} border-b ${cardBorder} px-4 sm:px-6 lg:px-8 py-4`}>
          <div className="max-w-[1600px] mx-auto flex items-center justify-between flex-wrap gap-3">
            <h1 className={`text-lg font-semibold ${textPrimary} flex items-center gap-2.5`}>
              <FileText className="h-5 w-5 text-indigo-400 shrink-0" />
              <span className="truncate">Adwin — working reconciliation position</span>
            </h1>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-lg border transition-colors ${darkMode ? 'border-[hsl(225,15%,22%)] text-zinc-400 hover:text-white' : 'border-gray-300 text-gray-500 hover:text-gray-700'}`}
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <SharedClaimV3
            payload={data}
            title={meta?.title || 'Adwin — working reconciliation position'}
            description={meta?.description || null}
            createdAt={meta?.createdAt || new Date().toISOString()}
            dark={darkMode}
          />
        </div>
      </div>
    );
  }

  const colSpan = hasAcData ? 12 : 11;

  return (
    <div className={`min-h-screen ${bg} transition-colors`}>
      {/* Header */}
      <div className={`${cardBg} border-b ${cardBorder} px-4 sm:px-6 lg:px-8 py-4`}>
        <div className="max-w-[1600px] mx-auto flex items-center justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <h1 className={`text-xl sm:text-2xl font-bold ${textPrimary} flex items-center gap-2.5`}>
              <FileText className="h-6 w-6 text-indigo-400 shrink-0" />
              <span className="truncate">{meta?.title || 'Shared Report'}</span>
            </h1>
            {meta?.description && <p className={`text-sm ${textSecondary} mt-0.5`}>{meta.description}</p>}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className={`text-xs ${textMuted} flex items-center gap-3`}>
              <span>Period: {data.period.recon}{data.period.postRecon ? ` + ${data.period.postRecon}` : ''}</span>
              <span>Up to {meta?.lastMonth}</span>
              {meta?.createdAt && <span>Generated {new Date(meta.createdAt).toLocaleDateString()}</span>}
            </div>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-lg border transition-colors ${darkMode ? 'border-[hsl(225,15%,22%)] text-zinc-400 hover:text-white' : 'border-gray-300 text-gray-500 hover:text-gray-700'}`}
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        {/* Search + count */}
        <div className="flex items-center gap-3">
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search partner, brand, account..."
            className={`px-3 py-1.5 w-72 text-xs rounded-lg border focus:outline-none focus:ring-1 focus:ring-indigo-500/50 ${inputBg}`} />
          <span className={`text-xs ${textMuted}`}>{filteredRows.length} accounts</span>
        </div>

        {/* Table */}
        <div className={`${cardBg} rounded-xl border ${cardBorder} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className={`${headBg} sticky top-0`}>
                <tr>
                  <th className="w-8 px-2 py-2.5"></th>
                  <SortHeader field="partner" label="Partner / Brand" />
                  <th className={`px-3 py-2.5 text-left font-medium ${textMuted}`}>GEO</th>
                  <SortHeader field="accountId" label="Account" />
                  <SortHeader field="reconInvoiced" label="Recon Inv." align="right" />
                  <SortHeader field="income" label="Recon Income" align="right" />
                  <th className={`px-3 py-2.5 text-left font-medium ${textMuted}`}>Source</th>
                  <SortHeader field="postReconInvoiced" label="Post-Recon Inv." align="right" />
                  {hasAcData && <SortHeader field="acPostRecon" label="AC Income" align="right" className={darkMode ? 'text-indigo-400/70' : 'text-indigo-600'} />}
                  <SortHeader field="totalInvoiced" label="Total Inv." align="right" />
                  <SortHeader field="totalEarned" label="Total Earned" align="right" className={darkMode ? 'text-emerald-400/70' : 'text-emerald-600'} />
                  <SortHeader field="underpaid" label="Underpaid" align="right" />
                  <SortHeader field="pct" label="%" align="right" />
                </tr>
              </thead>
              <tbody className={`divide-y ${divider}`}>
                {filteredRows.map((row, idx) => {
                  const key = `${row.issue.accountId}-${row.issue.offer}-${idx}`;
                  const isExpanded = expandedRows.has(key);
                  const entries = row.issue.invoiceEntries;
                  const monthly = sortMonthly(row.issue.invoiceMonthly || []);
                  const hasEntries = entries && entries.length > 0;
                  const acM = acMonthlyMap.get(row.issue.accountId) || [];
                  const hasAcMonthly = acM.length > 0;
                  const hasReconOrSynced = (row.issue.reconMonthly?.length || 0) > 0 || (row.issue.syncedMonthly?.length || 0) > 0;
                  const hasDrilldown = hasEntries || monthly.length > 0 || hasReconOrSynced || hasAcMonthly;

                  return (
                    <React.Fragment key={key}>
                      <tr className={`${rowHover} cursor-pointer`} onClick={() => toggleRow(key)}>
                        <td className={`px-2 py-2 ${textMuted}`}>
                          {hasDrilldown ? (isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />) : null}
                        </td>
                        <td className="px-3 py-2">
                          <div className={`${textPrimary} font-medium`}>{row.issue.partner}</div>
                          <div className={`${textMuted} text-[10px]`}>{row.issue.brand}{row.issue.offer ? ` — ${row.issue.offer}` : ''}</div>
                        </td>
                        <td className={`px-3 py-2 font-medium ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>{row.issue.geo}</td>
                        <td className="px-3 py-2">
                          <div className={`${textValue} tabular-nums`}>{row.issue.accountId}</div>
                          <div className={`${textMuted} text-[10px] truncate max-w-[140px]`}>{row.issue.username}</div>
                          {row.issue.adwstatsAccountName && (
                            <div className={`text-[10px] truncate max-w-[140px] ${darkMode ? 'text-emerald-500/70' : 'text-emerald-600/70'}`} title={row.issue.adwstatsAccountName}>
                              ↔ {row.issue.adwstatsAccountName}
                            </div>
                          )}
                        </td>
                        <td className={`px-3 py-2 text-right ${textValue} tabular-nums`}>{fmtCurrency(row.reconInvoiced)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          <div className={darkMode ? 'text-amber-400' : 'text-amber-600'}>{fmtCurrency(row.incomeAmount)}</div>
                          {row.incomeSource !== 'Reconciliation' && (
                            <div className={`text-[9px] ${textMuted}`}>was {fmtCurrency(row.incomeOriginal)}</div>
                          )}
                          {row.incomeOriginalCurrency && row.incomeOriginalAmount != null && (
                            <div className={`text-[9px] ${textMuted}`}>
                              {fmtOriginal(row.incomeOriginalAmount, row.incomeOriginalCurrency)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium ${sourceColor(row.incomeSource)}`}>
                            {sourceShort(row.incomeSource)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {row.postReconInvoiced > 0 ? <span className={textValue}>{fmtCurrency(row.postReconInvoiced)}</span> : <span className={textMuted}>—</span>}
                        </td>
                        {hasAcData && (
                          <td className="px-3 py-2 text-right tabular-nums">
                            {row.acPostRecon > 0 ? <span className={darkMode ? 'text-indigo-400' : 'text-indigo-600'}>{fmtCurrency(row.acPostRecon)}</span> : <span className={textMuted}>—</span>}
                          </td>
                        )}
                        <td className={`px-3 py-2 text-right ${textValue} tabular-nums`}>{fmtCurrency(row.totalInvoiced)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-medium ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{fmtCurrency(row.totalEarned)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-medium ${darkMode ? 'text-red-400' : 'text-red-600'}`}>{fmtCurrency(row.underpaid)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums ${darkMode ? 'text-red-400' : 'text-red-600'}`}>{row.underpaidPct.toFixed(1)}%</td>
                      </tr>

                      {/* Drilldown */}
                      {isExpanded && hasDrilldown && (() => {
                        const reconM = row.issue.reconMonthly || [];
                        const syncedM = row.issue.syncedMonthly || [];
                        const hasComparison = reconM.length > 0 || syncedM.length > 0 || acM.length > 0;

                        const invoiceByMonth = new Map<string, InvoiceMonth>();
                        if (hasEntries && entries) {
                          for (const entry of entries) {
                            for (const m of (entry.monthly || [])) {
                              const mk = monthToSortKey(m.month);
                              const existing = invoiceByMonth.get(mk);
                              if (existing) { existing.ftds += m.ftds; existing.revenue += m.revenue; }
                              else invoiceByMonth.set(mk, { month: m.month, ftds: m.ftds, revenue: m.revenue });
                            }
                          }
                        } else {
                          for (const m of monthly) invoiceByMonth.set(monthToSortKey(m.month), m);
                        }

                        const allMonthsSet = new Set<string>();
                        invoiceByMonth.forEach((_, k) => allMonthsSet.add(k));
                        reconM.forEach(m => allMonthsSet.add(monthToSortKey(m.month)));
                        syncedM.forEach(m => allMonthsSet.add(monthToSortKey(m.month)));
                        acM.forEach(m => allMonthsSet.add(monthToSortKey(m.month)));
                        const allMonths = [...allMonthsSet].sort();

                        const reconByMonth = new Map(reconM.map(m => [monthToSortKey(m.month), m]));
                        const syncedByMonth = new Map(syncedM.map(m => [monthToSortKey(m.month), m]));
                        const acByMonth = new Map(acM.map(m => [monthToSortKey(m.month), m]));

                        return (
                          <tr>
                            <td colSpan={colSpan} className={`${drillBg} px-6 py-3`}>
                              <div className="space-y-4">
                                {hasEntries && entries && (
                                  <>
                                    <div className={`text-[10px] ${textMuted} font-medium uppercase tracking-wider`}>
                                      Invoice Breakdown — {row.issue.partner} / {row.issue.brand}
                                    </div>
                                    {entries.map((entry, ei) => {
                                      const sorted = sortMonthly(entry.monthly || []);
                                      return (
                                        <div key={ei} className={`border ${drillBorder} rounded-lg p-3`}>
                                          <div className="flex items-center justify-between mb-2">
                                            <span className={`text-[11px] font-medium truncate max-w-[500px] ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>{entry.label}</span>
                                            <span className={`text-[11px] ${textValue} tabular-nums font-medium`}>{fmtCurrency(entry.revenue)}</span>
                                          </div>
                                          {sorted.length > 0 && (
                                            <table className="w-full max-w-xl text-xs">
                                              <thead><tr className={textMuted}>
                                                <th className="text-left py-1 px-2 font-medium">Month</th>
                                                <th className="text-right py-1 px-2 font-medium">FTDs</th>
                                                <th className="text-right py-1 px-2 font-medium">Revenue (EUR)</th>
                                              </tr></thead>
                                              <tbody className={`divide-y ${divider}`}>
                                                {sorted.map(m => (
                                                  <tr key={m.month} className={textValue}>
                                                    <td className="py-1 px-2">{fmtMonth(m.month)}</td>
                                                    <td className="py-1 px-2 text-right tabular-nums">{m.ftds.toLocaleString()}</td>
                                                    <td className="py-1 px-2 text-right tabular-nums">{fmtCurrency(m.revenue)}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </>
                                )}

                                {hasComparison && allMonths.length > 0 && (
                                  <div className={`border ${drillBorder} rounded-lg p-3`}>
                                    <div className={`text-[10px] ${textMuted} font-medium uppercase tracking-wider mb-2`}>Monthly Comparison</div>
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-xs">
                                        <thead><tr className={textMuted}>
                                          <th className="text-left py-1 px-2 font-medium">Month</th>
                                          <th className="text-right py-1 px-2 font-medium">Invoice</th>
                                          <th className={`text-right py-1 px-2 font-medium ${darkMode ? 'text-amber-400/70' : 'text-amber-600'}`}>Recon</th>
                                          {syncedM.length > 0 && <th className={`text-right py-1 px-2 font-medium ${darkMode ? 'text-emerald-400/70' : 'text-emerald-600'}`}>Synced(-5%)</th>}
                                          {acM.length > 0 && <th className={`text-right py-1 px-2 font-medium ${darkMode ? 'text-indigo-400/70' : 'text-indigo-600'}`}>AC</th>}
                                          <th className={`text-right py-1 px-2 font-medium ${darkMode ? 'text-red-400/70' : 'text-red-600'}`}>Diff</th>
                                        </tr></thead>
                                        <tbody className={`divide-y ${divider}`}>
                                          {allMonths.map(m => {
                                            const inv = invoiceByMonth.get(m);
                                            const rec = reconByMonth.get(m);
                                            const syn = syncedByMonth.get(m);
                                            const ac = acByMonth.get(m);
                                            const invAmt = inv?.revenue || 0;
                                            const acAmt = ac?.commission || 0;
                                            const bestIncome = Math.max(rec?.earnings || 0, (syn?.commission || 0) * 0.95, acAmt);
                                            const diff = invAmt > 0 && bestIncome > 0 ? invAmt - bestIncome : 0;
                                            const diffColor = Math.abs(diff) < 1 ? textMuted : diff < 0 ? (darkMode ? 'text-emerald-400' : 'text-emerald-600') : (darkMode ? 'text-red-400' : 'text-red-600');
                                            return (
                                              <tr key={m} className={textValue}>
                                                <td className="py-1 px-2">{fmtMonth(m)}</td>
                                                <td className="py-1 px-2 text-right tabular-nums">{invAmt > 0 ? fmtCurrency(invAmt) : <span className={textMuted}>—</span>}</td>
                                                <td className={`py-1 px-2 text-right tabular-nums ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>{rec ? fmtCurrency(rec.earnings) : <span className={textMuted}>—</span>}</td>
                                                {syncedM.length > 0 && <td className={`py-1 px-2 text-right tabular-nums ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{syn ? fmtCurrency(syn.commission * 0.95) : <span className={textMuted}>—</span>}</td>}
                                                {acM.length > 0 && <td className={`py-1 px-2 text-right tabular-nums ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{acAmt > 0 ? fmtCurrency(acAmt) : <span className={textMuted}>—</span>}</td>}
                                                <td className={`py-1 px-2 text-right tabular-nums ${diffColor}`}>{Math.abs(diff) > 0.01 ? fmtCurrency(diff) : <span className={textMuted}>—</span>}</td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                                {row.acPostRecon > 0 && (
                                  <div className={`border ${darkMode ? 'border-indigo-500/20' : 'border-indigo-200'} rounded-lg p-3`}>
                                    <div className={`text-[10px] font-medium uppercase tracking-wider mb-1 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                                      AC Post-Recon Earnings (Dec 2024+)
                                    </div>
                                    <span className={`text-sm font-bold tabular-nums ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{fmtCurrency(row.acPostRecon)}</span>
                                    <span className={`text-[10px] ${textMuted} ml-2`}>from Affiliation Cloud reports</span>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })()}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredRows.length === 0 && (
            <div className={`p-8 text-center text-sm ${textMuted}`}>No accounts found</div>
          )}
        </div>

        {/* Totals footer */}
        <div className={`${cardBg} rounded-xl border ${darkMode ? 'border-red-500/20' : 'border-red-200'} p-4`}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className={`text-sm ${textSecondary}`}>
              <strong className={textPrimary}>{totals.count}</strong> underpaid accounts
            </div>
            <div className="flex items-center gap-6 text-sm flex-wrap">
              <div>
                <span className={textMuted}>Invoiced: </span>
                <span className={`${textPrimary} tabular-nums font-medium`}>{fmtCurrency(totals.totalInvoiced)}</span>
              </div>
              <div>
                <span className={textMuted}>Earned: </span>
                <span className={`tabular-nums font-medium ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{fmtCurrency(totals.totalEarned)}</span>
              </div>
              <div>
                <span className={textMuted}>Underpaid: </span>
                <span className={`tabular-nums font-bold ${darkMode ? 'text-red-400' : 'text-red-600'}`}>{fmtCurrency(totals.underpaid)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`text-center text-xs ${textMuted} py-4`}>
          Generated by Adwstats{meta?.createdAt ? ` on ${new Date(meta.createdAt).toLocaleString()}` : ''}
        </div>
      </div>
    </div>
  );
};

export default SharedReport;
